import { useEffect } from 'react'
import { LayoutGrid, List, Package, Users, Settings, ShoppingBag, ShoppingCart } from 'lucide-react'
import { usePOSStore } from '@/stores/posStore'
import { useIsMobile } from '@/hooks'
import { useHeldOrders, useEcomOrders, useDeliveryOrders } from '@/api/receipts'
import { usePOSConfig } from '@/api/config'
import type { ActiveTab } from '@/types'

export function TabsBar() {
  const { activeTab, viewMode, cart, setActiveTab, setViewMode } = usePOSStore()
  const isMobile = useIsMobile()

  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0)

  // Surface a red dot on the Orders tab when held or ecom orders are
  // waiting for staff attention. These hooks are already used inside the
  // Orders tab; React Query dedupes the network calls, so subscribing
  // here costs nothing.
  //
  // Both lists drop refund receipts (is_return / src_id / return_receipt_id)
  // and the ecom list also drops kiosk_status === 'Completed' — same
  // filters OrdersTab applies, so the dot, the tab counts, and the
  // rendered lists all stay in sync.
  const { data: heldOrders } = useHeldOrders()
  const { data: ecomOrders } = useEcomOrders()
  // Prefetched on page load so the Deliveries tab + its count are warm,
  // same as ecom/held. The BE list is already scoped to outstanding
  // delivery orders, so its presence also drives the Orders alert dot.
  // Gated on the store's "Allow Delivery" setting — when delivery is off
  // there's no Deliveries tab and nothing to alert on.
  const { data: posConfig } = usePOSConfig()
  const { data: deliveryOrders } = useDeliveryOrders(posConfig?.posAllowDelivery ?? false)
  type OrderAlertCandidate = {
    kiosk_status?: string | null
    delivery_status?: string | null
    is_return?: boolean
    src_id?: number | null
    return_receipt_id?: number | null
  }
  const isRefundReceipt = (o: OrderAlertCandidate): boolean =>
    o.is_return === true || !!o.src_id || !!o.return_receipt_id
  const pendingHeldCount =
    heldOrders?.filter((o: OrderAlertCandidate) => !isRefundReceipt(o)).length ?? 0
  const pendingEcomCount =
    ecomOrders?.filter(
      (o: OrderAlertCandidate) =>
        o.kiosk_status !== 'Completed' && !isRefundReceipt(o),
    ).length ?? 0
  // Delivered / picked-up orders stay on the Deliveries tab but are done,
  // so they don't count toward the "orders waiting" alert dot.
  const pendingDeliveryCount =
    deliveryOrders?.filter(
      (o: OrderAlertCandidate) =>
        !isRefundReceipt(o)
        && o.delivery_status !== 'delivered'
        && o.delivery_status !== 'pickup_at_store',
    ).length ?? 0
  const hasOrdersAlert =
    pendingHeldCount > 0 || pendingEcomCount > 0 || pendingDeliveryCount > 0

  // When resizing from mobile to desktop while on cart tab, switch to products
  useEffect(() => {
    if (!isMobile && activeTab === 'cart') {
      setActiveTab('products')
    }
  }, [isMobile, activeTab, setActiveTab])

  // Keyboard shortcuts: P, C, F, O
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return
      }

      if (!e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        switch (e.key.toLowerCase()) {
          case 'p':
            e.preventDefault()
            setActiveTab('products')
            break
          case 'c':
            e.preventDefault()
            setActiveTab('customers')
            break
          case 'f':
            e.preventDefault()
            setActiveTab('functions')
            break
          case 'o':
            e.preventDefault()
            setActiveTab('orders')
            break
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [setActiveTab])

  const tabs: Array<{ id: ActiveTab; label: string; icon: typeof Package; shortcut?: string }> = [
    { id: 'products', label: 'Products', icon: Package, shortcut: 'P' },
    { id: 'customers', label: 'Customers', icon: Users, shortcut: 'C' },
    { id: 'orders', label: 'Orders', icon: ShoppingBag, shortcut: 'O' },
    { id: 'functions', label: 'Functions', icon: Settings, shortcut: 'F' },
    ...(isMobile ? [{ id: 'cart' as ActiveTab, label: 'Cart', icon: ShoppingCart }] : []),
  ]

  return (
    <div className="bg-white border-b border-gray-200 flex-shrink-0 overflow-visible">
      <div className="flex items-center justify-between pr-0 md:pr-2">
        <div className="flex flex-1 md:flex-initial">
          {tabs.map(({ id, label, icon: Icon, shortcut }) => (
            <button
              key={id}
              tabIndex={-1}
              onClick={() => setActiveTab(id)}
              className={`flex items-center justify-center md:justify-start gap-2 px-3 md:px-6 pt-4 pb-2.5 md:py-3 font-medium border-b-2 transition-all cursor-pointer focus:outline-none flex-1 md:flex-initial relative ${
                activeTab === id
                  ? 'border-green-500 text-green-600 bg-green-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon size={20} />
              <span className="hidden md:inline">{label}</span>
              {shortcut && (
                <kbd
                  className={`hidden md:inline-flex ml-1 px-1.5 py-0 rounded-md text-[10px] font-semibold ${
                    activeTab === id
                      ? 'bg-green-100 border border-green-400 text-green-600'
                      : 'bg-white border border-gray-300 text-gray-500'
                  }`}
                >
                  {shortcut}
                </kbd>
              )}
              {/* Cart count badge on mobile */}
              {id === 'cart' && cartItemCount > 0 && (
                <span className="absolute top-1.5 left-1/2 translate-x-1 md:hidden bg-green-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1">
                  {cartItemCount}
                </span>
              )}
              {/* Held / ecom orders waiting indicator */}
              {id === 'orders' && hasOrdersAlert && (
                <span
                  className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white"
                  title="Orders waiting in On Hold, Ecom, or Deliveries"
                />
              )}
            </button>
          ))}
        </div>

        {/* View Toggle */}
        <div className="hidden md:flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-1.5 rounded-md transition-all cursor-pointer ${
              viewMode === 'grid'
                ? 'bg-white text-green-600 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <LayoutGrid size={18} />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-1.5 rounded-md transition-all cursor-pointer ${
              viewMode === 'list'
                ? 'bg-white text-green-600 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <List size={18} />
          </button>
        </div>
      </div>
    </div>
  )
}
